import { useMemo } from 'react';
import { useLocation, Link } from 'react-router-dom';
import { AIBox } from '@crazyegginc/hatch';

import { usePermissions, useCrazyAI, useAuthContext, useUserLanguage } from '/src/hooks';
import { FEATURES, AI_PROMPTS } from '/src/features/_global/constants';
import { DashboardPaywall } from '/src/features/_global/paywalls/DashboardPaywall';

import { Section } from '../Section';
import { TrendMetric, TrendMetricSection } from '../TrendMetric';
import { PlayButton } from '/src/components/PlayButton';
import { round } from '/src/utils/math';
import { EnableRecordingsWall } from '/src/features/recordings/components/dashboard/paywalls/EnableRecordingsWall';

import { ReactComponent as RecordingsIcon } from '@crazyegginc/hatch/dist/images/icon-recording-filled.svg';
import { useSelectedSource } from '/src/components/source-selector/source-selector';

export function RecordingsSection({ data, loading }) {
  return (
    <Section className="col-span-1 row-span-2 self-start">
      <Section.Header icon={RecordingsIcon} color="text-wisteria-500" to="/recordings">
        <span>Recordings</span>
      </Section.Header>
      <Section.Body>
        <Content data={data} loading={loading} />
      </Section.Body>
    </Section>
  );
}

function Content({ data, loading }) {
  const permissions = usePermissions();
  const { allowed: canViewDashboard, reason } = permissions.can('view', FEATURES.RECORDINGS);

  if (!canViewDashboard) {
    return (
      <div className="p-5">
        <DashboardPaywall feature={FEATURES.RECORDINGS} reason={reason} mini={true} />
      </div>
    );
  }

  return <RecordingsContent data={data} loading={loading} />;
}

function RecordingsContent({ data, loading }) {
  const { selectedSource } = useSelectedSource();

  const location = useLocation();
  const { currentUser } = useAuthContext();
  const userPreferredLanguage = useUserLanguage();
  const permissions = usePermissions();
  const canViewAiSummary = permissions.can('summarize', FEATURES.AI).allowed;

  const params = useMemo(
    () => ({
      data: data?.recordings.latestSessions,
      language: userPreferredLanguage,
    }),
    [data, userPreferredLanguage],
  );

  const context = useMemo(
    () => ({
      url: `${location.pathname}${location.search}`,
      userId: currentUser?.userId,
    }),
    [location.pathname, location.search, currentUser?.userId],
  );

  const latestSessionsExist = useMemo(() => {
    if (data?.recordings.latestSessions) {
      return JSON.parse(data.recordings.latestSessions).length > 0;
    }
    return false;
  }, [data]);

  const {
    data: aiData,
    error,
    refetch,
  } = useCrazyAI({
    type: AI_PROMPTS.RECORDING_SUGGESTIONS,
    params,
    context,
    enabled: latestSessionsExist && canViewAiSummary,
  });

  const suggestions = useMemo(() => {
    if (aiData?.ready) {
      try {
        const parsed = JSON.parse(aiData.text);
        return (
          <>
            <div className="text-body-1 mb-2 flex items-center">Recordings You Should Watch:</div>
            {parsed.map((x) => (
              <div className="mb-2 flex" key={x.recording_id}>
                <PlayButton hashedId={x.recording_id} valid={true} sizeClass="h-6 w-6" />
                <span className="ml-3 leading-tight">{x.reason}</span>
              </div>
            ))}
          </>
        );
      } catch {
        return { error: true };
      }
    }
    return { ready: false };
  }, [aiData]);

  if (selectedSource && !selectedSource.sessionRecording) {
    return (
      <div className="p-5">
        <EnableRecordingsWall mini={true} />
      </div>
    );
  }

  return (
    <>
      <TrendMetricSection>
        <TrendMetric
          text="Watched"
          extraText={
            <div className="text-xs">{`(${
              data?.recordings.total ? round((data.recordings.watched / data.recordings.total) * 100, 1) : '0'
            }% of total)`}</div>
          }
          value={data?.recordings.watched}
          loading={loading}
        />
      </TrendMetricSection>
      {!canViewAiSummary ? (
        <div className="m-5">
          <Link to="/recordings" className="text-link">
            View recordings
          </Link>
        </div>
      ) : !loading && !latestSessionsExist ? null : (
        <div className="p-5">
          <AIBox
            id="recording_suggestions"
            loading={!aiData?.ready}
            error={error || suggestions?.error}
            refetch={refetch}
            showChangeLanguage={true}
          >
            {suggestions}
          </AIBox>
        </div>
      )}
    </>
  );
}
